import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";

import { makeRequest } from "@/utils/makeRequest";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { queryClient } from "@/services/queryClient";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import {
  ArrowLeft,
  Eye,
  Pause,
  Play,
  Plus,
  Save,
  Share2,
  Trash,
  Trash2,
} from "lucide-react";

import { useAd, useProfile } from "@/hooks";

import Loader from "@/components/loader/Loader";
import { ErrorState } from "@/components/ErrorState";
import { useAppSelector } from "@/store/hooks";

import { PriceType } from "@/interfaces/exchange";
import { AdStatus, type Advertisement } from "@/interfaces/advertisement";

import { formatEvmHumanBalance, normalizeCurrency, safeDecimal } from "@/utils";

import { formatCurrency } from "@/utils/formatCurrency";

import { AdminVerificationDialog } from "@/components/admin-components/AdminVerificationDialog";
import PaymentMethod from "@/components/user/PaymentMethod";

/**
 * Keep crypto quantities as strings.
 *
 * Do NOT use parseFloat for BTC/USDT/USDC amounts.
 *
 * Examples:
 * "0.007"
 * "0.000001"
 * "100.25"
 * "1"
 */
type FormTerm = {
  title: string;
  description: string;
};

type AdvertisementForm = {
  priceType: PriceType;
  price: string;
  floatingMargin: string;
  minAmount: string;
  maxAmount: string;
  adjustmentAmount: string;
  paymentWindow: string;
  paymentMethodIds: string[];
  terms: FormTerm[];
};

export default function EditAdvertisementPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id ?? "";

  const { data: ad, isLoading, isError, refetch } = useAd(id as string, userId);
  const { data: profile } = useProfile(userId);

  const reopenFormRef = useRef<HTMLDivElement | null>(null);

  console.log("AD", ad);

  const [verification, setVerification] = useState<null | {
    type: "UPDATE" | "REOPEN" | "CLOSE" | "PAUSE" | "ACTIVATE";
    payload?: any;
  }>(null);

  const [adjustmentType, setAdjustmentType] = useState<"ADD" | "REMOVE">("ADD");

  const [reopenAmount, setReopenAmount] = useState("");

  const [form, setForm] = useState<AdvertisementForm>({
    priceType: PriceType.FIXED,
    price: "",
    floatingMargin: "",
    minAmount: "",
    maxAmount: "",
    adjustmentAmount: "",
    paymentWindow: "15",
    paymentMethodIds: [],
    terms: [],
  });

  const activePaymentMethods = useMemo(() => {
    return profile?.paymentMethods?.filter((method) => method.active) ?? [];
  }, [profile?.paymentMethods]);

  const status = ad?.status;
  const isClosed = status === AdStatus.CLOSED;
  const canEdit = status === AdStatus.ACTIVE || status === AdStatus.PAUSED;
  const canPause = status === AdStatus.ACTIVE;
  const canActivate = status === AdStatus.PAUSED;
  const canDelete = status !== AdStatus.CLOSED;
  const canReopen = isClosed;

  const activeTradeAmount = safeDecimal(
    (ad as any)?.activeTradeAmount ?? (ad as any)?.reservedTradeAmount ?? "0",
  );

  const hasActiveTrades = Number(activeTradeAmount ?? 0) > 0;

  useEffect(() => {
    if (!ad) return;

    let normalizedTerms: FormTerm[] = [];

    try {
      if (!ad.terms) {
        normalizedTerms = [];
      } else if (typeof ad.terms === "string") {
        const parsed = JSON.parse(ad.terms);

        if (Array.isArray(parsed)) {
          normalizedTerms = parsed.map((term: any) => ({
            title: term?.title ?? "",
            description: term?.description ?? "",
          }));
        }
      } else if (Array.isArray(ad.terms)) {
        normalizedTerms = ad.terms.map((term) => ({
          title: term.title ?? "",
          description: term.description ?? "",
        }));
      }
    } catch {
      normalizedTerms = [];
    }

    /**
     * IMPORTANT:
     *
     * Use only the payment methods currently attached
     * to this advertisement.
     *
     * Do NOT automatically select every active payment
     * method from the user's profile.
     */
    const adPaymentMethodIds = Array.isArray(ad.paymentMethods)
      ? ad.paymentMethods
          .map((pm: any) => (typeof pm === "string" ? pm : pm?.id))
          .filter(
            (id: unknown): id is string =>
              typeof id === "string" && id.length > 0,
          )
      : [];

    setForm({
      priceType: ad.priceType ?? PriceType.FIXED,
      price: ad.price != null ? String(ad.price) : "",
      floatingMargin:
        ad.floatingMargin != null ? String(ad.floatingMargin) : "",
      minAmount: ad.minAmount != null ? String(ad.minAmount) : "",
      maxAmount: ad.maxAmount != null ? String(ad.maxAmount) : "",

      /**
       * Adjustment is ALWAYS a delta.
       *
       * Example:
       * +0.5 BTC = increase reserve by 0.5 BTC
       * -0.2 BTC = decrease reserve by 0.2 BTC
       *
       * It must NEVER contain the advertisement's
       * current totalAmount.
       */
      adjustmentAmount: "",
      paymentWindow: ad.paymentWindow != null ? String(ad.paymentWindow) : "15",
      paymentMethodIds: adPaymentMethodIds,
      terms: normalizedTerms,
    });
  }, [ad]);

  const handleChange = <K extends keyof AdvertisementForm>(
    key: K,
    value: AdvertisementForm[K],
  ) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleDecimalChange = (
    key:
      | "price"
      | "floatingMargin"
      | "minAmount"
      | "maxAmount"
      | "adjustmentAmount",
    value: string,
  ) => {
    /**
     * Allow:
     *
     * ""
     * "0"
     * "0."
     * "0.007"
     * "100.25"
     *
     * Do not use parseFloat.
     */
    if (!/^\d*(\.\d*)?$/.test(value)) {
      return;
    }

    handleChange(key, value);
  };

  const handleReopenAmountChange = (value: string) => {
    if (!/^\d*(\.\d*)?$/.test(value)) {
      return;
    }

    setReopenAmount(value);
  };

  const buildUpdateAdPayload = () => {
    const payload: Record<string, any> = {};

    const price = safeDecimal(form.price);

    if (price !== undefined) {
      payload.price = price;
    }

    payload.priceType = form.priceType;

    const minAmount = safeDecimal(form.minAmount);

    if (minAmount !== undefined) {
      payload.minAmount = minAmount;
    }

    const maxAmount = safeDecimal(form.maxAmount);

    if (maxAmount !== undefined) {
      payload.maxAmount = maxAmount;
    }

    const adjustment = safeDecimal(form.adjustmentAmount);

    if (adjustment !== undefined && adjustment !== "0") {
      payload.adjustmentAmount =
        adjustmentType === "ADD" ? adjustment : `-${adjustment}`;
    }

    const floatingMargin = safeDecimal(form.floatingMargin);

    if (floatingMargin !== undefined) {
      payload.floatingMargin = floatingMargin;
    }

    const paymentWindow = Number(form.paymentWindow);

    if (
      Number.isInteger(paymentWindow) &&
      paymentWindow >= 5 &&
      paymentWindow <= 60
    ) {
      payload.paymentWindow = paymentWindow;
    }

    payload.paymentMethodIds = form.paymentMethodIds;

    payload.terms = form.terms.map((term, index) => ({
      title: term.title.trim(),

      description: term.description.trim(),

      order: index,
    }));

    /**
     * DO NOT SEND:
     *
     * assetId
     * fiatCurrencyId
     * type
     * totalAmount
     * available
     * status
     * userId
     * createdAt
     * updatedAt
     *
     * The backend owns those values.
     */

    return payload;
  };

  const updateAdMutation = useMutation({
    mutationFn: async (payload: Record<string, any>) => {
      if (!id) {
        throw new Error("Advertisement ID is missing.");
      }

      const res = await makeRequest.put(`/advertisement/${id}`, payload);

      return res.data;
    },

    onSuccess: async () => {
      toast.success("Advertisement updated successfully.");

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["ad", id],
        }),

        queryClient.invalidateQueries({
          queryKey: ["ad", id, userId],
        }),

        queryClient.invalidateQueries({
          queryKey: ["profile", userId],
        }),

        queryClient.invalidateQueries({
          queryKey: ["advertisments:all"],
        }),
      ]);

      await refetch();
    },

    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  function calculateAdValue(
    advertisement: Advertisement | undefined,
    amount: string | number,
  ) {
    if (!advertisement) {
      return "—";
    }

    const numericAmount = Number(amount);

    const numericPrice = Number(advertisement.price ?? 0);

    if (!Number.isFinite(numericAmount) || !Number.isFinite(numericPrice)) {
      return "—";
    }

    const currency = normalizeCurrency(advertisement?.fiatCurrency?.code ?? "");

    const converted = (numericAmount * numericPrice).toFixed(2);

    return `${currency} ${formatCurrency(converted)}`;
  }

  const save = () => {
    if (ad?.status === AdStatus.PAUSED) {
      toast.warning(
        "This advertisement is paused. Activate it before making changes.",
      );
      return;
    }

    if (!canEdit) {
      toast.error("This advertisement cannot be edited.");
      return;
    }

    if (!activePaymentMethods.length) {
      toast.error("At least one payment method must be setup.");
      return;
    }

    const min = Number(form.minAmount);

    const max = Number(form.maxAmount);

    if (!Number.isFinite(min) || min <= 0) {
      toast.error("Minimum amount must be greater than zero.");
      return;
    }

    if (!Number.isFinite(max) || max <= 0) {
      toast.error("Maximum amount must be greater than zero.");
      return;
    }

    if (min > max) {
      toast.error("Minimum amount cannot be greater than maximum amount.");
      return;
    }

    // if (max > Number(ad?.available)) {
    //   toast.error(
    //     "The entered max amount is greater than the advertisement available amount.",
    //   );

    //   return;
    // }

    if (form.priceType === PriceType.FIXED) {
      const numericPrice = Number(form.price);

      if (!Number.isFinite(numericPrice) || numericPrice <= 0) {
        toast.error("Fixed price must be greater than zero.");
        return;
      }
    }

    const adjustment = Number(form.adjustmentAmount || 0);

    if (!Number.isFinite(adjustment) || adjustment < 0) {
      toast.error("Adjustment amount cannot be negative.");
      return;
    }

    if (adjustment > 0 && adjustmentType === "REMOVE") {
      const currentAvailable = Number(ad?.available ?? 0);

      if (adjustment > currentAvailable) {
        toast.error(
          "You cannot decrease the reserve by more than the available advertisement amount.",
        );
        return;
      }
    }

    if (form.paymentMethodIds.length === 0) {
      toast.error("Select at least one payment method.");
      return;
    }

    const payload = buildUpdateAdPayload();

    setVerification({
      type: "UPDATE",
      payload,
    });
  };

  const pauseAd = () => {
    if (!canPause) return;

    setVerification({
      type: "PAUSE",
    });
  };

  const activateAd = () => {
    if (!canActivate) return;

    setVerification({
      type: "ACTIVATE",
    });
  };

  const closeAd = () => {
    if (!canDelete) return;

    setVerification({
      type: "CLOSE",
    });
  };

  const scrollToReopenForm = () => {
    reopenFormRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    // Focus the amount input after scrolling
    setTimeout(() => {
      document.getElementById("reopen-amount")?.focus();
    }, 500);
  };

  const reopenAd = () => {
    if (!canReopen) {
      return;
    }

    const amount = safeDecimal(reopenAmount);

    if (amount === undefined || amount === "0") {
      toast.error("Enter a valid reserve amount greater than zero.");
      return;
    }

    const min = safeDecimal(form.minAmount);
    const max = safeDecimal(form.maxAmount);

    if (min === undefined || min === "0") {
      toast.error("Minimum amount must be greater than zero.");
      return;
    }

    if (max === undefined || max === "0") {
      toast.error("Maximum amount must be greater than zero.");
      return;
    }

    if (Number(min) > Number(max)) {
      toast.error("Minimum amount cannot be greater than maximum amount.");
      return;
    }

    if (form.priceType === PriceType.FIXED) {
      const price = safeDecimal(form.price);

      if (price === undefined || price === "0") {
        toast.error("Fixed price must be greater than zero.");
        return;
      }
    }

    if (form.paymentMethodIds.length === 0) {
      toast.error("Select at least one payment method.");
      return;
    }

    /*
     * REOPEN BEHAVIOR
     *
     * The advertisement is CLOSED.
     *
     * We do not allow the user to modify its trading configuration
     * during reopening.
     *
     * The existing values already came from `ad` and were loaded
     * into `form`.
     *
     * The ONLY new value supplied by the user is `reopenAmount`.
     */

    const payload = {
      // Existing advertisement configuration
      priceType: form.priceType,
      price: safeDecimal(form.price),
      floatingMargin: safeDecimal(form.floatingMargin),
      minAmount: safeDecimal(form.minAmount),
      maxAmount: safeDecimal(form.maxAmount),
      paymentWindow: Number(form.paymentWindow),
      paymentMethodIds: form.paymentMethodIds,

      terms: form.terms.map((term, index) => ({
        title: term.title.trim(),
        description: term.description.trim(),
        order: index,
      })),

      // Explicit CLOSED -> ACTIVE transition
      reopen: true,

      // New reserve amount
      adjustmentAmount: amount,
    };

    setVerification({
      type: "REOPEN",
      payload,
    });
  };

  const shareAd = async () => {
    if (!ad || !id) return;

    const shareUrl = `${window.location.origin}/p2p/ads/${id}`;

    const shareText =
      `Trade ${normalizeCurrency(ad.asset?.currency ?? "")} at ` +
      `${normalizeCurrency(ad.fiatCurrency?.code ?? "")} ${formatCurrency(
        ad.price ?? 0,
      )}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${ad.asset?.currency} P2P Advertisement`,
          text: shareText,
          url: shareUrl,
        });

        return;
      } catch {
        // User cancelled share sheet.
      }
    }

    await navigator.clipboard.writeText(shareUrl);

    toast.success("Advertisement link copied.");
  };

  if (isLoading) {
    return <Loader type="bounce" />;
  }

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="min-h-screen bg-background sm:p-8">
      <div className="max-w-8xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-4">
          {/* BACK */}

          <Button
            className="hidden sm:flex"
            variant="outline"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <Button
            variant="outline"
            //  onClick={() => setShareOpen(true)}
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
        </div>

        {/* HEADER */}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Edit Advertisement
            </h1>

            <p className="text-sm text-muted-foreground font-mono">
              ID: {id} • Status:{" "}
              <span className="capitalize font-medium">
                {status?.toLowerCase()}
              </span>
            </p>
          </div>

          {/* ACTION BAR */}

          <div className="flex gap-2 flex-wrap">
            {canPause && (
              <Button variant="outline" onClick={pauseAd}>
                <Pause className="w-4 h-4 mr-2" />
                Pause
              </Button>
            )}

            {canActivate && (
              <Button variant="outline" onClick={activateAd}>
                <Play className="w-4 h-4 mr-2" />
                Activate
              </Button>
            )}

            {canReopen && (
              <Button variant="outline" onClick={scrollToReopenForm}>
                <Play className="w-4 h-4 mr-2" />
                Reopen
              </Button>
            )}

            {canDelete && (
              <Button variant="destructive" onClick={closeAd}>
                <Trash className="w-4 h-4 mr-2" />
                Close
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-5 w-full">
          <div className="flex-1">
            <Card className="-mx-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Advertisement Details
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* BASIC INFO */}

                <div className="flex items-center justify-between gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Type</p>

                    <p className="capitalize">{ad?.type}</p>
                  </div>

                  <div>
                    <p className="text-muted-foreground">Status</p>

                    <p className="capitalize">{ad?.status}</p>
                  </div>
                </div>

                {/* ASSET / FIAT */}

                <div className="flex items-center justify-between gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Asset</p>

                    <p>{normalizeCurrency(ad?.asset?.currency ?? "")}</p>
                  </div>

                  <div>
                    <p className="text-muted-foreground">Fiat</p>

                    <p>
                      {ad?.fiatCurrency?.iso2} {ad?.fiatCurrency?.name} (
                      {ad?.fiatCurrency?.code})
                    </p>
                  </div>
                </div>

                {/* PRICE */}

                <div>
                  <p className="text-muted-foreground text-sm mb-1">
                    Price Configuration
                  </p>

                  <div className="bg-muted/50 p-4 rounded-lg">
                    <p className="font-mono text-lg font-semibold">
                      {ad?.priceType === PriceType.FIXED
                        ? `${normalizeCurrency(
                            ad?.fiatCurrency?.code ?? "",
                          )} ${formatCurrency(ad?.price ?? 0)}`
                        : `${ad?.floatingMargin}% floating`}
                    </p>

                    <p className="text-xs text-muted-foreground mt-1">
                      {ad?.priceType === PriceType.FIXED
                        ? "Fixed Price"
                        : "Market + Margin"}
                    </p>
                  </div>
                </div>

                {/* LIMITS */}

                <div>
                  <p className="text-muted-foreground text-sm mb-2">
                    Current Limits ({ad?.asset?.currency})
                  </p>

                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-muted/50 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground">MIN</p>

                      <p className="font-semibold">
                        {formatEvmHumanBalance(
                          Number(ad?.minAmount),
                          ad?.asset?.token?.isNative ?? false,
                        )}
                      </p>

                      <p className="text-xs text-muted-foreground mt-1">
                        {calculateAdValue(ad, String(ad?.minAmount ?? "0"))}
                      </p>
                    </div>

                    <div className="bg-muted/50 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground">MAX</p>

                      <p className="font-semibold">
                        {formatEvmHumanBalance(
                          Number(ad?.maxAmount),
                          ad?.asset?.token?.isNative ?? false,
                        )}
                      </p>

                      <p className="text-xs text-muted-foreground mt-1">
                        {calculateAdValue(ad, String(ad?.maxAmount ?? "0"))}
                      </p>
                    </div>

                    <div className="bg-muted/50 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground">AVAILABLE</p>

                      <p className="font-semibold">
                        {formatEvmHumanBalance(
                          Number(ad?.available),
                          ad?.asset?.token?.isNative ?? false,
                        )}{" "}
                      </p>

                      <p className="text-xs text-muted-foreground mt-1">
                        {calculateAdValue(ad, String(ad?.available ?? "0"))}
                      </p>
                    </div>
                  </div>
                </div>

                {/* INVENTORY */}

                <div>
                  <p className="text-muted-foreground text-sm mb-2">
                    Advertisement Inventory
                  </p>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-muted/50 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground">TOTAL</p>

                      <p className="font-semibold">
                        {ad?.totalAmount ?? "0"}{" "}
                        {normalizeCurrency(ad?.asset?.currency ?? "")}
                      </p>
                    </div>

                    <div className="bg-muted/50 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground">AVAILABLE</p>

                      <p className="font-semibold">
                        {ad?.available ?? "0"}{" "}
                        {normalizeCurrency(ad?.asset?.currency ?? "")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* TERMS */}

                <div>
                  <p className="text-muted-foreground text-sm mb-2">
                    Terms &amp; Conditions
                  </p>

                  {Array.isArray(ad?.terms) && ad.terms.length > 0 ? (
                    <div className="space-y-3">
                      {ad.terms.map((term: any, idx: number) => (
                        <div
                          key={idx}
                          className="border rounded-lg p-3 bg-card"
                        >
                          <p className="font-medium text-sm">{term.title}</p>

                          <p className="text-sm text-muted-foreground mt-1">
                            {term.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">
                      No terms specified
                    </p>
                  )}
                </div>

                {/* STATS */}

                <div className="pt-4 border-t">
                  <p className="text-muted-foreground text-sm mb-2">Activity</p>

                  <div className="flex items-center justify-between gap-4 text-sm">
                    <div>
                      Completed Trades:{" "}
                      <span className="font-semibold">
                        {ad?.trades?.length ?? 0}
                      </span>
                    </div>

                    <div>
                      Payment Window:{" "}
                      <span className="font-semibold">
                        {ad?.paymentWindow} min
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {isClosed && (
            <div ref={reopenFormRef}>
              <Card className="-mx-4 border-amber-500/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Play className="w-5 h-5" />
                    Reopen Advertisement
                  </CardTitle>

                  <p className="text-sm text-muted-foreground">
                    This advertisement is closed. To reopen it, configure the
                    advertisement and provide the amount of asset you want to
                    commit to the new trading reserve.
                  </p>
                </CardHeader>

                <CardContent className="space-y-5">
                  <div className="rounded-lg border bg-muted/30 p-4">
                    <p className="text-sm font-medium">Current reserve</p>

                    <p className="text-2xl font-semibold mt-1">
                      0 {normalizeCurrency(ad?.asset?.currency ?? "")}
                    </p>

                    <p className="text-xs text-muted-foreground mt-1">
                      The previous reserve was released when this advertisement
                      was closed.
                    </p>
                  </div>

                  <div>
                    <Label className="mb-2">New Advertisement Reserve</Label>

                    <Input
                      id="reopen-amount"
                      type="text"
                      inputMode="decimal"
                      value={reopenAmount}
                      onChange={(e) => handleReopenAmountChange(e.target.value)}
                      placeholder="0.00000000"
                      className="!h-12"
                    />

                    <p className="text-xs text-muted-foreground mt-2">
                      Enter the amount of{" "}
                      {normalizeCurrency(ad?.asset?.currency ?? "")} you want to
                      commit to this advertisement.
                    </p>

                    {reopenAmount && (
                      <p className="text-sm font-semibold text-muted-foreground mt-2">
                        {calculateAdValue(ad, reopenAmount)}
                      </p>
                    )}
                  </div>

                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
                    <p className="text-sm font-medium">
                      What happens when you reopen?
                    </p>

                    <ul className="text-xs text-muted-foreground mt-2 space-y-1 list-disc pl-5">
                      <li>Your available wallet balance will be validated.</li>
                      <li>
                        The requested reserve will be locked for this
                        advertisement.
                      </li>
                      <li>
                        The advertisement will become active only after the
                        transaction succeeds.
                      </li>
                      <li>
                        The backend will perform the operation atomically.
                      </li>
                    </ul>
                  </div>

                  <Button
                    onClick={reopenAd}
                    disabled={!reopenAmount}
                    size="lg"
                    className="w-full"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Reopen Advertisement
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="flex-1">
            <Card className="h-full -mx-4">
              <CardHeader>
                <CardTitle>Update Advertisement</CardTitle>

                <p className="text-sm text-muted-foreground">
                  Modify trading parameters below.
                </p>

                {hasActiveTrades && (
                  <p className="text-sm text-amber-600 mt-2">
                    This advertisement has active trade exposure. Trading terms
                    are protected until those trades are completed.
                  </p>
                )}
              </CardHeader>

              <CardContent className="space-y-8">
                {/* =================================================
                    TRADING CONFIGURATION
                ================================================= */}

                <div className=" ring-1 ring-gray-700 p-3 rounded-md">
                  <h3 className="font-semibold mb-4">Trading Configuration</h3>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* TYPE */}

                    <div>
                      <Label className="mb-2">Trade Type</Label>

                      <Input
                        value={ad?.type ?? ""}
                        disabled
                        className="!h-12"
                      />
                    </div>

                    {/* PRICE TYPE */}

                    <div>
                      <Label className="mb-2">Price Type</Label>

                      <Select
                        value={form.priceType}
                        onValueChange={(value) =>
                          handleChange("priceType", value as PriceType)
                        }
                        disabled={!canEdit || isClosed || hasActiveTrades}
                      >
                        <SelectTrigger className="!h-12 w-full">
                          <SelectValue />
                        </SelectTrigger>

                        <SelectContent>
                          <SelectItem value={PriceType.FIXED}>Fixed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* PRICE */}

                    {form.priceType === PriceType.FIXED && (
                      <div>
                        <Label className="mb-2">
                          Price (
                          {normalizeCurrency(ad?.fiatCurrency?.code ?? "")})
                        </Label>

                        <Input
                          type="text"
                          inputMode="decimal"
                          disabled={!canEdit || isClosed || hasActiveTrades}
                          value={form.price}
                          onChange={(e) =>
                            handleDecimalChange("price", e.target.value)
                          }
                          className="!h-12"
                        />
                      </div>
                    )}

                    {/* MIN */}

                    <div className="flex flex-col">
                      <Label className="mb-1">Min Amount</Label>

                      <Input
                        type="text"
                        inputMode="decimal"
                        disabled={!canEdit || isClosed || hasActiveTrades}
                        value={form.minAmount}
                        onChange={(e) =>
                          handleDecimalChange("minAmount", e.target.value)
                        }
                        className="!h-12"
                      />

                      <div className="text-muted-foreground text-sm font-semibold mt-1">
                        {calculateAdValue(ad, form.minAmount)}
                      </div>
                    </div>

                    {/* MAX */}

                    <div className="flex flex-col">
                      <Label className="mb-1">Max Amount</Label>

                      <Input
                        type="text"
                        inputMode="decimal"
                        disabled={!canEdit || isClosed || hasActiveTrades}
                        value={form.maxAmount}
                        onChange={(e) =>
                          handleDecimalChange("maxAmount", e.target.value)
                        }
                        className="!h-12"
                      />

                      <div className="text-muted-foreground text-sm font-semibold mt-1">
                        {calculateAdValue(ad, form.maxAmount)}
                      </div>
                    </div>

                    {/* PAYMENT WINDOW */}

                    <div className="flex flex-col">
                      <Label className="mb-1">Payment Window (min)</Label>

                      <Input
                        type="number"
                        min={5}
                        max={60}
                        disabled={!canEdit || isClosed || hasActiveTrades}
                        value={form.paymentWindow}
                        onChange={(e) =>
                          handleChange("paymentWindow", e.target.value)
                        }
                        className="!h-12"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2 ring-1 ring-gray-700 p-3 rounded-md">
                  <Label className="mb-1">Adjust Advertisement Reserve</Label>

                  <Select
                    value={adjustmentType}
                    onValueChange={(value) =>
                      setAdjustmentType(value as "ADD" | "REMOVE")
                    }
                    disabled={!canEdit || isClosed}
                  >
                    <SelectTrigger className="!h-12 w-full">
                      <SelectValue />
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value="ADD">+ Increase Reserve</SelectItem>

                      <SelectItem value="REMOVE">− Decrease Reserve</SelectItem>
                    </SelectContent>
                  </Select>

                  <p className="text-xs text-muted-foreground">
                    {adjustmentType === "ADD"
                      ? "Amount of additional asset you want to commit to this advertisement."
                      : "Amount of currently available advertisement inventory you want to release."}
                  </p>

                  <Input
                    type="text"
                    inputMode="decimal"
                    min={0}
                    disabled={!canEdit || isClosed}
                    value={form.adjustmentAmount}
                    onChange={(e) =>
                      handleDecimalChange("adjustmentAmount", e.target.value)
                    }
                    className="!h-12"
                    placeholder="0.00000000"
                  />

                  <div className="text-muted-foreground text-sm font-semibold">
                    {calculateAdValue(ad, form.adjustmentAmount || "0")}
                  </div>

                  <p className="text-xs text-muted-foreground">
                    This changes the advertisement reserve, not the maximum size
                    of an individual trade.
                  </p>
                </div>

                {/* PAYMENT METHODS */}

                <div className="-mx-6">
                  <PaymentMethod />
                </div>

                {/* =================================================
                    TERMS
                ================================================= */}

                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold">Terms &amp; Conditions</h3>

                    <Button
                      disabled={!canEdit}
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleChange("terms", [
                          ...form.terms,
                          {
                            title: "",
                            description: "",
                          },
                        ])
                      }
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Term
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {form.terms.map((term, index) => (
                      <div
                        key={index}
                        className="border p-5 rounded-xl space-y-3 bg-card"
                      >
                        <Input
                          disabled={!canEdit}
                          value={term.title}
                          onChange={(e) => {
                            const updated = [...form.terms];

                            updated[index] = {
                              ...updated[index],
                              title: e.target.value,
                            };

                            handleChange("terms", updated);
                          }}
                          placeholder="Term title"
                          className="!h-12"
                        />

                        <Textarea
                          disabled={!canEdit}
                          value={term.description}
                          onChange={(e) => {
                            const updated = [...form.terms];

                            updated[index] = {
                              ...updated[index],
                              description: e.target.value,
                            };

                            handleChange("terms", updated);
                          }}
                          placeholder="Term description"
                          rows={3}
                        />

                        <Button
                          disabled={!canEdit}
                          variant="ghost"
                          className="text-red-500 hover:text-red-600"
                          onClick={() =>
                            handleChange(
                              "terms",
                              form.terms.filter((_, idx) => idx !== index),
                            )
                          }
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Remove
                        </Button>
                      </div>
                    ))}

                    {form.terms.length === 0 && (
                      <p className="text-muted-foreground text-sm italic py-4">
                        No terms added yet. Click "Add Term" to create trading
                        rules.
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>

              {/* SAVE */}

              <div className="p-6 border-t flex justify-end">
                <Button
                  onClick={save}
                  disabled={updateAdMutation.isPending || !canEdit}
                  size="lg"
                >
                  <Save className="w-4 h-4 mr-2" />

                  {updateAdMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <AdminVerificationDialog
        open={!!verification}
        onOpenChange={(open) => {
          if (!open) {
            setVerification(null);
          }
        }}
        onSuccess={async ({ otp, securityCode }) => {
          if (!verification) {
            return;
          }

          const { type, payload } = verification;

          try {
            if (type === "UPDATE") {
              await updateAdMutation.mutateAsync({
                ...payload,
                otp,
                securityCode,
              });

              await queryClient.invalidateQueries({
                queryKey: ["ad", id, userId],
              });

              queryClient.invalidateQueries({
                queryKey: ["wallet-asset", ad?.assetId, userId],
              });

              queryClient.invalidateQueries({
                queryKey: ["wallet", userId],
              });
            }

            if (type === "REOPEN") {
              await updateAdMutation.mutateAsync({
                ...payload,
                reopen: true,
                otp,
                securityCode,
              });

              toast.success("Advertisement reopened successfully.");

              await Promise.all([
                queryClient.invalidateQueries({
                  queryKey: ["ad", id],
                }),

                queryClient.invalidateQueries({
                  queryKey: ["ad", id, userId],
                }),

                queryClient.invalidateQueries({
                  queryKey: ["profile", userId],
                }),

                queryClient.invalidateQueries({
                  queryKey: ["advertisments:all"],
                }),

                queryClient.invalidateQueries({
                  queryKey: ["wallet-asset", ad?.assetId, userId],
                }),

                queryClient.invalidateQueries({
                  queryKey: ["wallet", userId],
                }),
              ]);

              setReopenAmount("");

              await refetch();
            }

            if (type === "CLOSE") {
              await makeRequest.put(`/advertisement/${id}/close`, {
                otp,
                securityCode,
              });

              toast.success("Advertisement closed.");

              await Promise.all([
                queryClient.invalidateQueries({
                  queryKey: ["ad", id],
                }),

                queryClient.invalidateQueries({
                  queryKey: ["ad", id, userId],
                }),

                queryClient.invalidateQueries({
                  queryKey: ["profile", userId],
                }),

                queryClient.invalidateQueries({
                  queryKey: ["advertisments:all"],
                }),

                queryClient.invalidateQueries({
                  queryKey: ["wallet-asset", ad?.assetId, userId],
                }),

                queryClient.invalidateQueries({
                  queryKey: ["wallet", userId],
                }),
              ]);

              await refetch();

              // navigate("/dashboard/p2p/ads");
            }

            // =================================================
            // PAUSE
            // =================================================

            if (type === "PAUSE") {
              await makeRequest.put(`/advertisement/${id}/pause`, {
                otp,
                securityCode,
              });

              toast.success("Advertisement paused.");

              await queryClient.invalidateQueries({
                queryKey: ["ad", id, userId],
              });

              queryClient.invalidateQueries({
                queryKey: ["wallet", userId],
              });

              await refetch();
            }

            // =================================================
            // ACTIVATE
            // =================================================

            if (type === "ACTIVATE") {
              await makeRequest.put(`/advertisement/${id}/activate`, {
                otp,
                securityCode,
              });

              toast.success("Advertisement activated.");

              await queryClient.invalidateQueries({
                queryKey: ["ad", id, userId],
              });

              (queryClient.invalidateQueries({
                queryKey: ["wallet-asset", ad?.assetId, userId],
              }),
                queryClient.invalidateQueries({
                  queryKey: ["wallet", userId],
                }),
                await refetch());
            }
          } catch (error) {
            toast.error(getErrorMessage(error));
          } finally {
            setVerification(null);
          }
        }}
      />
    </div>
  );
}
